import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { Message, Table } from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import '../assets/theme.css';

import LoadingDataEnhancer from './LoadingData';
import withPagination from './WithPagination';
import GuaranteePurchasesV2 from './GuaranteePurchasesV2';
import GuaranteePurchasesList from '../views/GuaranteePurchases/List';
import Search from '../views/Shared/Search';
import GuaranteePurchaseEdit from '../views/GuaranteePurchases/EditForm';
import GuaranteePurchaseExtend from '../views/GuaranteePurchases/ExtendForm';
import GuaranteePurchaseCancel from '../views/GuaranteePurchases/CancelForm';

import guaranteePurchasesQuery from '../graphql/guaranteePurchasesQuery';
import updateGuaranteePurchaseMutation from '../graphql/updateGuaranteePurchaseMutation';
import cancelGuaranteePurchaseMutation from '../graphql/cancelGuaranteePurchaseMutation';
import { clearSearch, setSearch } from '../data/searchField/actions';
import { setRefreshSearch } from '../data/refreshSearch/actions';
import APP_PATH from '../constants/paths';
import { PRODUCT_FAMILY_ALL } from '../constants/productFamily';
import { setPage } from '../data/page/actions';
import AuditModal from '../components/AuditModal';
import guaranteePurchaseAuditsQuery from '../graphql/guaranteePurchaseAuditsQuery';
import { useGuaranteesApiV2 } from '../hooks/useGuaranteePurchases';

const GuaranteePurchases = ({
  data: { guaranteePurchases = [], refetch },
  searchFields,
  refreshSearch,
  clearSearchFields,
  dispatchRefreshSearch,
  setSearchFields: setSearchFieldsAction,
  setPage: setPageAction,
  updateMutation,
  cancelMutation,
  history,
}) => {
  const [selectedGuaranteePurchase, setSelectedGuaranteePurchase] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [editFormError, setEditFormError] = useState(null);
  const [editFormSubmitting, setEditFormSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelFormError, setCancelFormError] = useState(null);
  const [cancelFormSubmitting, setCancelFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendFormError, setExtendFormError] = useState(null);
  const [extendFormSubmitting, setExtendFormSubmitting] = useState(false);

  useEffect(() => {
    if (refreshSearch) {
      dispatchRefreshSearch(false);
      clearSearchFields({});
      const variableArguments = {
        vin: null,
        dealerNumbers: [],
      };
      refetch({
        ...variableArguments,
      });
    }
  }, [refreshSearch, dispatchRefreshSearch, clearSearchFields, refetch]);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onEditFormCloseHandler = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setEditFormError(null);
    setEditFormSubmitting(null);
  }, []);

  const onAuditCloseHandler = useCallback(() => {
    setIsAuditModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onExtendFormCloseHandler = useCallback(() => {
    setIsExtendModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setExtendFormError(null);
    setExtendFormSubmitting(null);
  }, []);

  const onCancelFormCloseHandler = useCallback(() => {
    setIsCancelModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onSelectGuarantee = useCallback(
    (action, guarantee) => {
      setSelectedGuaranteePurchase(guarantee);

      switch (action) {
        case 'Edit':
          setIsEditModalOpen(true);
          setIsCancelModalOpen(false);
          break;
        case 'Extend Guarantee':
          setIsExtendModalOpen(true);
          setExtendFormError(null);
          setExtendFormSubmitting(null);
          setIsCancelModalOpen(false);
          setIsEditModalOpen(false);
          break;
        case 'Cancel Guarantee':
          setIsCancelModalOpen(true);
          setIsEditModalOpen(false);
          setCancelFormError(null);
          setCancelFormSubmitting(null);
          break;
        case 'Audit':
          setIsAuditModalOpen(true);
          setIsEditModalOpen(false);
          setCancelFormError(null);
          setCancelFormSubmitting(null);
          break;
        case 'Vehicle Status Tracker':
          history.push(
            APP_PATH.VEHICLE_STATUS_TRACKER.replace(
              ':purchaseId',
              guarantee.id
            )
          );
          break;
        default:
          setSelectedGuaranteePurchase(null);
      }
    },
    [history]
  );

  const onEditFormSubmitHandler = useCallback(
    values => {
      const purchaseInput = { guaranteePurchase: { ...values } };
      setEditFormSubmitting(true);
      updateMutation({
        variables: { purchaseInput },
      })
        .then(({ data: mutationData }) => {
          const {
            updateGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            setEditFormSubmitting(false);
            setFlashMessage('Successfully Updated Guarantee Purchase');
            refetch();
            setPageAction({});
            onEditFormCloseHandler();
          } else {
            setEditFormSubmitting(false);
            setEditFormError(errors);
          }
        })
        .catch(() => {
          setEditFormSubmitting(false);
          setEditFormError('Error while updating Guarantee Purchase');
        });
    },
    [updateMutation, refetch, setPageAction, onEditFormCloseHandler]
  );

  const onExtendFormSubmitHandler = useCallback(
    values => {
      const purchaseInput = { guaranteePurchase: { ...values } };
      setExtendFormSubmitting(true);
      updateMutation({
        variables: { purchaseInput },
      })
        .then(({ data: mutationData }) => {
          const {
            updateGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            setExtendFormSubmitting(false);
            setFlashMessage('Successfully extended Guarantee Purchase');
            onExtendFormCloseHandler();
          } else {
            setExtendFormSubmitting(false);
            setExtendFormError(errors);
          }
        })
        .catch(() => {
          setExtendFormSubmitting(false);
          setExtendFormError('Error while extending Guarantee Purchase');
        });
    },
    [updateMutation, onExtendFormCloseHandler]
  );

  const onCancelFormSubmitHandler = useCallback(
    values => {
      setCancelFormSubmitting(true);
      const cancelInput = { ...values, productFamily: PRODUCT_FAMILY_ALL };
      cancelMutation({
        variables: { cancelInput },
      })
        .then(({ data: mutationData }) => {
          const {
            cancelGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            refetch();
            setIsCancelModalOpen(false);
            setSelectedGuaranteePurchase(null);
            setCancelFormSubmitting(null);
            setFlashMessage('Successfully Cancelled Guarantee Purchase');
          } else {
            setCancelFormSubmitting(false);
            setCancelFormError(errors);
          }
        })
        .catch(() => {
          setCancelFormSubmitting(false);
          setCancelFormError('Error while cancelling Guarantee Purchase');
        });
    },
    [cancelMutation, refetch]
  );

  const onSearchSubmitHandler = useCallback(
    event => {
      const { vin, dealerNumbers } = event.target;
      const dealerNos = isEmpty(dealerNumbers.value)
        ? null
        : [dealerNumbers.value.trim()];
      const variableArguments = {
        vin: vin.value.trim() || null,
        dealerNumbers: dealerNos,
      };
      refetch({
        ...variableArguments,
      });

      setSearchFieldsAction(variableArguments);
      setPageAction({});
    },
    [refetch, setSearchFieldsAction, setPageAction]
  );

  const renderAuditRow = auditRecord => {
    return (
      <>
        {auditRecord.event === 'update'
          && `${
            auditRecord.versionAuthor
          } updated the Guarantee Purchase - ${
            auditRecord.itemType
          } details`}
        {auditRecord.event === 'create'
          && `${
            auditRecord.versionAuthor
          } created the Guarantee Purchase - ${auditRecord.itemType}`}
        {auditRecord.event === 'destroy'
          && `${
            auditRecord.versionAuthor
          } Destroyed Guarantee Purchase - ${auditRecord.itemType}`}
      </>
    );
  };

  const renderChangesetRow = (auditRecord, change, key) => {
    if (key !== 'updated_at' && auditRecord.event === 'update' && change[1] !== null && change[1] !== '') {
      return (
        <Table.Row key={key}>
          <Table.Cell>
            <span>
              set{' '}
              <span style={{ fontWeight: 'bold' }}>
                {key}{' '}
              </span>
              from{' '}
              <span style={{ fontWeight: 'bold' }}>
                {' '}
                {change[0] || 'nil'}{' '}
              </span>
              to{' '}
              <span style={{ fontWeight: 'bold' }}>
                {change[1]}{' '}
              </span>
            </span>
          </Table.Cell>
        </Table.Row>
      );
    }
    return null;
  };

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumbers']}
      />
      <GuaranteePurchasesList
        onSelectGuarantee={onSelectGuarantee}
        guaranteePurchases={guaranteePurchases}
      />

      {selectedGuaranteePurchase && isEditModalOpen && (
        <div>
          <GuaranteePurchaseEdit
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={editFormError}
            submitting={editFormSubmitting}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isCancelModalOpen && (
        <div>
          <GuaranteePurchaseCancel
            isCancelModalOpen={isCancelModalOpen}
            onCloseHandler={onCancelFormCloseHandler}
            onSubmitHandler={onCancelFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={cancelFormError}
            submitting={cancelFormSubmitting}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isAuditModalOpen && (
        <div>
          <AuditModal
            header='Status History - Guarantee Purchases'
            fetchAuditRecordQuery={guaranteePurchaseAuditsQuery}
            queryVariables={{ guaranteePurchaseId: parseInt(selectedGuaranteePurchase.id, 10) }}
            auditResponseAttribute='guaranteePurchaseAudits'
            renderAuditRow={renderAuditRow}
            renderChangesetRow={renderChangesetRow}
            isAuditModalOpen={isAuditModalOpen}
            onCloseHandler={onAuditCloseHandler}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isExtendModalOpen && (
        <div>
          <GuaranteePurchaseExtend
            isModalOpen={isExtendModalOpen}
            onCloseHandler={onExtendFormCloseHandler}
            onSubmitHandler={onExtendFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={extendFormError}
            submitting={extendFormSubmitting}
          />
        </div>
      )}
    </div>
  );
};

GuaranteePurchases.propTypes = {
  data: PropTypes.shape({
    guaranteePurchases: PropTypes.array,
    refetch: PropTypes.func,
  }),
  searchFields: PropTypes.shape({
    vin: PropTypes.string,
    dealerNumbers: PropTypes.array,
  }).isRequired,
  refreshSearch: PropTypes.bool,
  clearSearchFields: PropTypes.func.isRequired,
  dispatchRefreshSearch: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  updateMutation: PropTypes.func,
  cancelMutation: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

GuaranteePurchases.defaultProps = {
  data: {},
  refreshSearch: false,
  updateMutation: null,
  cancelMutation: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  dispatchRefreshSearch: value => {
    dispatch(setRefreshSearch(value));
  },
  clearSearchFields: () => {
    dispatch(clearSearch());
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields, refreshSearch },
  } = state;
  return {
    searchFields,
    refreshSearch,
  };
};

const V1Container = compose(
  withRouter,
  withPagination(guaranteePurchasesQuery, 'guaranteePurchases'),
  graphql(updateGuaranteePurchaseMutation, { name: 'updateMutation' }),
  graphql(cancelGuaranteePurchaseMutation, { name: 'cancelMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(GuaranteePurchases);

const GuaranteePurchasesContainer = props => {
  const [isApiV2Enabled, loaded] = useGuaranteesApiV2();
  if (!loaded) return null;

  return React.createElement(
    isApiV2Enabled ? GuaranteePurchasesV2 : V1Container,
    props
  );
};

export default GuaranteePurchasesContainer;
